import { useState } from "react";
import { Button, Form, Message, Modal } from "semantic-ui-react";
import { EMPTY_ITEM, validateChargeItem } from "./billingForms";

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

/* Add or edit one item. `item` null = add. */
export default function ChargeItemModal({ item, categories, onSave, onClose }) {
  const [form, setForm] = useState(() => (item ? { ...EMPTY_ITEM, ...item } : { ...EMPTY_ITEM }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (key) => (_, d) => setForm((f) => ({ ...f, [key]: d.type === "checkbox" ? d.checked : d.value }));

  const save = async () => {
    const errs = validateChargeItem(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await onSave(form, item?.id || null);
      onClose();
    } catch (e) {
      // Pin server field errors (e.g. a duplicate name) to their inputs.
      const fe = e.fieldErrors || {};
      setErrors({
        name: fe.name?.[0], categoryId: fe.category?.[0], unitPrice: fe.unit_price?.[0],
        save: Object.keys(fe).length ? "" : e.message || "Could not save the item.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open size="small" onClose={onClose}>
      <Modal.Header>{item ? `Edit ${item.name}` : "Add item"}</Modal.Header>
      <Modal.Content>
        <Form onSubmit={save}>
          <Form.Group widths="equal">
            <Form.Input
              id="item-name" label="Item name" required placeholder="e.g. Breakfast"
              value={form.name} onChange={set("name")} error={fieldError(errors.name)}
            />
            <Form.Select
              label="Bill heading" required placeholder="Choose…"
              options={categories.map((c) => ({ key: c.id, value: c.id, text: c.name }))}
              value={form.categoryId} onChange={set("categoryId")} error={fieldError(errors.categoryId)}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              id="item-price" label="Price before GST (₹)" required inputMode="decimal" placeholder="e.g. 150"
              value={form.unitPrice} onChange={set("unitPrice")} error={fieldError(errors.unitPrice)}
            />
            <Form.Input
              id="item-unit" label="Unit (optional)" placeholder="plate, cup, bottle…"
              value={form.unitLabel} onChange={set("unitLabel")}
            />
          </Form.Group>
          <Form.Checkbox
            id="item-gst"
            label="Charge GST on this item (never charged on official bookings)"
            checked={form.gstApplicable} onChange={set("gstApplicable")}
          />
          <Form.Checkbox
            id="item-grid"
            label="Show as a row in the daily meals grid"
            checked={form.inDailyGrid} onChange={set("inDailyGrid")}
          />
          <Form.Checkbox
            id="item-active"
            label="In use (switch off to stop it being added to new bills)"
            checked={form.isActive} onChange={set("isActive")}
          />
          <Form.Group widths="equal">
            <Form.Input
              id="item-hsn" label="HSN/SAC code (optional)" placeholder="e.g. 996331"
              value={form.hsnSac} onChange={set("hsnSac")}
            />
            <Form.Input
              id="item-order" label="Position in lists" type="number" min={0}
              value={form.order} onChange={set("order")}
            />
          </Form.Group>
        </Form>
        {errors.save && <Message negative content={errors.save} />}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button primary onClick={save} loading={saving} disabled={saving}>Save item</Button>
      </Modal.Actions>
    </Modal>
  );
}
